import { BroadcastMessage } from '@app/core/service';
import { BroadcastPopupService } from './../../../service/broadcast-popup.service';
import { Component, OnInit, OnDestroy, ChangeDetectorRef, ViewChild, inject } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ByocCourseService } from '../../service/course.service';
import { ByocCourseStepMissionListComponent } from './list/list.component';
import { ByocMissionService } from '@app/byoc/popup/mission/service/mission.service';
import { ByocService } from '@app/byoc/popup/service/byoc.service';
import { filter, tap } from 'rxjs/operators';
import { MessageService } from '@app/service/message.service';
import { TranslateService } from '@ngx-translate/core';
import { ByocEpisodePopupService } from '@app/byoc/popup/episode/service/popup.service';
import { control } from '../../../utils';

enum origin {
    all = 0,
    st = 1,
    custom = 2,
}

@Component({
    selector: 'app-byoc-course-step-mission',
    templateUrl: './mission.component.html',
    styleUrls: ['./mission.component.scss'],
    providers: [ByocMissionService],
    standalone: false
})
export class ByocCourseStepMissionComponent implements OnInit, OnDestroy {
    private readonly changeDetectorRef = inject(ChangeDetectorRef);
    private readonly byocCourseService = inject(ByocCourseService);
    private readonly broadcastPopupService = inject(BroadcastPopupService);
    private readonly byocService = inject(ByocService);
    private readonly messageService = inject(MessageService);
    private readonly translateService = inject(TranslateService);
    private readonly byocEpisodePopupService = inject(ByocEpisodePopupService);

    @ViewChild('missionsList', { static: true })
    missionsList!: ByocCourseStepMissionListComponent;

    processing = false;
    selectedMissionLength = 0;
    creatingMission = false;

    /**
     * Form Controls
     */
    searchFormControl!: UntypedFormControl;
    originControl!: UntypedFormControl;

    private subscriptions: Subscription = new Subscription();
    private broadcastSubscription?: Subscription;

    get missionIdControl() {
        return control(this.byocCourseService.form.get('missionId'));
    }

    ngOnInit() {
        this.searchFormControl = new UntypedFormControl('');
        this.originControl = new UntypedFormControl(origin.all);

        this.subscriptions.add(this.byocCourseService.form.valueChanges.pipe(tap(() => this.changeDetectorRef.detectChanges())).subscribe());

        this.bindMissionChangeListener();
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
        if (this.broadcastSubscription) {
            this.broadcastSubscription.unsubscribe();
        }
    }

    bindMissionChangeListener() {
        this.subscriptions.add(
            this.missionIdControl.valueChanges
                .pipe(
                    filter((missionId) => missionId !== ''),
                    tap((missionId) => {
                        this.processing = true;
                        this.subscriptions.add(
                            this.byocService
                                .mission(missionId)
                                .pipe(
                                    tap((mission) => {
                                        this.selectedMissionLength = mission.courses ? mission.courses.length : 0;
                                        this.processing = false;
                                        this.changeDetectorRef.detectChanges();
                                    }),
                                )
                                .subscribe(),
                        );
                    }),
                )
                .subscribe(),
        );
    }

    cancelClick(): void {
        this.byocCourseService.close('btn-close');
    }

    backClick(): void {
        this.byocCourseService.step = 'general';
        this.changeDetectorRef.markForCheck();
    }

    nextClick(): void {
        if (this.missionIdControl.invalid) {
            return;
        }

        this.processing = true;

        this.byocCourseService.step = 'position';
        this.changeDetectorRef.markForCheck();
    }

    createCourseClick() {
        this.broadcastPopupService.send('hideCourse');
        this.creatingMission = true;
        this.broadcastSubscription = this.broadcastPopupService.broadcasts
            .pipe(
                tap((broadcastMessage: BroadcastMessage) => {
                    this.closeAddMissionPopup(broadcastMessage.message);
                }),
            )
            .subscribe();
    }

    saveClick(): void {
        this.byocCourseService.step = 'warning';
    }

    saveAndAddClick(): void {
        // set as publish
        this.byocCourseService.form.patchValue({ status: 2 });

        // show loader
        this.processing = true;
        this.changeDetectorRef.detectChanges();

        // save and create Episode
        this.subscriptions.add(
            this.byocService
                .createCourse(this.byocCourseService.form.getRawValue())
                .pipe(
                    tap((course) => {
                        this.messageService.setMessage(
                            this.translateService.instant('v3-byoc-popup-create-course-alert-created', { name: course.name }),
                            'info',
                        );
                        this.byocEpisodePopupService.replacePopup({
                            type: null,
                            courseId: course.id,
                            isFromOutside: true,
                            skipPosition: false,
                            uploadContentType: null,
                            episodeToEditId: null,
                            episodeToEditData: null,
                        });
                        this.changeDetectorRef.markForCheck();
                        this.changeDetectorRef.detectChanges();
                    }),
                )
                .subscribe(),
        );
    }

    /**
     * Close - is fired always when popup closes
     */
    private closeAddMissionPopup(mission: string): void {
        const missionObject = mission !== 'btn-close' && mission !== 'close' ? JSON.parse(mission) : null;
        this.broadcastSubscription?.unsubscribe();
        this.broadcastPopupService.send('showCourse');
        if (missionObject) {
            this.originControl.setValue(0);
            this.missionsList.newlyCreatedMissions.push(missionObject);
            this.missionsList.inactiveMissions.push(missionObject.id);
            this.missionIdControl.setValue(missionObject.id);
            this.missionsList.psRef?.scrollToTop();
            this.changeDetectorRef.detectChanges();
        }
        this.creatingMission = false;
    }
}
